import React, { useState } from 'react'
import {
  Box,
  Button,
  Card,
  TextField,
  MenuItem,
  Typography,
  Stack,
  Alert,
  Grid,
  Chip,

  IconButton,
  Tooltip,
  Paper,
  FormControl,
  InputLabel,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  LinearProgress,
} from '@mui/material'
import {
  Add as AddIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Check as ApproveIcon,
  Assessment as ReportIcon,
  Warning as WarningIcon,
  TrendingDown as TrendingDownIcon,

  Edit as EditIcon,
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import {
  useGetStockAdjustmentsQuery,
  useCreateStockAdjustmentMutation,
  useApproveStockAdjustmentMutation,
  useGetShrinkageReportQuery,
  useExportStockAdjustmentsMutation,
} from '../store/services/inventoryApi'
import { formatCompactCurrency } from '../utils/currency'

const StockAdjustmentsEnhanced: React.FC = () => {
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(20)

  
  // Filter states
  const [filters, setFilters] = useState({
    adjustment_type: 'all',
    status: 'all',
    start_date: '',
    end_date: '',
  })
  
  // Dialog states
  const [, setIsCreateOpen] = useState(false)
  const [selectedAdjustment, setSelectedAdjustment] = useState<any>(null)
  const [, setIsViewOpen] = useState(false)
  const [, setIsShrinkageReportOpen] = useState(false)
  
  const { enqueueSnackbar } = useSnackbar()

  // RTK Query hooks
  const { 
    data: adjustmentsData, 
    isLoading, 
    error,
    refetch 
  } = useGetStockAdjustmentsQuery({
    page: page + 1,
    per_page: pageSize,
    adjustment_type: filters.adjustment_type !== 'all' ? filters.adjustment_type : undefined,
    status: filters.status !== 'all' ? filters.status : undefined,
    start_date: filters.start_date || undefined,
    end_date: filters.end_date || undefined,
  })

  const { data: shrinkageData } = useGetShrinkageReportQuery({
    start_date: filters.start_date || undefined,
    end_date: filters.end_date || undefined,
  })

  const [] = useCreateStockAdjustmentMutation()
  const [approveAdjustment] = useApproveStockAdjustmentMutation()
  const [exportAdjustments] = useExportStockAdjustmentsMutation()

  const adjustments = adjustmentsData?.adjustments || []
  const totalRows = adjustmentsData?.total_count || 0

  // Debug logging
  React.useEffect(() => {
    if (adjustmentsData) {
      console.log('Stock Adjustments Data:', adjustmentsData)
      console.log('Adjustments Array:', adjustments)
      console.log('Total Rows:', totalRows)
    }
    if (error) {
      console.error('Stock Adjustments Error:', error)
    }
  }, [adjustmentsData, error])

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }))
    setPage(0) // Reset to first page when filters change
  }

  const handleApprove = async (adjustmentId: string) => {
    try {
      await approveAdjustment(adjustmentId).unwrap()
      enqueueSnackbar('Stock adjustment approved successfully', { variant: 'success' })
      refetch()
    } catch (error) {
      enqueueSnackbar('Failed to approve adjustment', { variant: 'error' })
    }
  }

  const handleExport = async (format: 'excel' | 'csv' = 'excel') => {
    try {
      const blob = await exportAdjustments({
        ...filters,
        format,
      }).unwrap()
      
      // Create download link
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `stock_adjustments_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'csv'}`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      
      enqueueSnackbar('Stock adjustments exported successfully', { variant: 'success' })
    } catch (error) {
      enqueueSnackbar('Failed to export adjustments', { variant: 'error' })
    }
  }

  const getAdjustmentTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      'damage': 'error',
      'loss': 'error',
      'theft': 'error',
      'found': 'success',
      'correction': 'info',
      'manual': 'primary',
      'return': 'success',
    }
    return colors[type] || 'default'
  }

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'pending': 'warning',
      'approved': 'success',
      'rejected': 'error',
    }
    return colors[status] || 'default'
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Paper 
        sx={{ 
          p: 3, 
          mb: 3, 
          background: 'linear-gradient(135deg, #2196f3 0%, #0d47a1 100%)',
          color: 'white',
          borderRadius: 3,
          boxShadow: '0 4px 16px rgba(33, 150, 243, 0.3)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
              Stock Adjustments
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Track and manage inventory adjustments with full audit trail
            </Typography>
          </Box>
          
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setIsCreateOpen(true)}
              size="small"
              sx={{ 
                color: 'white',
                borderColor: 'rgba(255,255,255,0.5)',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              New Adjustment
            </Button>
            <Button
              variant="outlined"
              startIcon={<ReportIcon />}
              onClick={() => setIsShrinkageReportOpen(true)}
              size="small"
              sx={{ 
                color: 'white',
                borderColor: 'rgba(255,255,255,0.5)',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              Shrinkage Report
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => handleExport('excel')}
              size="small"
              sx={{ 
                color: 'white',
                borderColor: 'rgba(255,255,255,0.5)',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              Export
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* Summary Cards */}
      {shrinkageData && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                p: 3, 
                borderRadius: 3,
                background: 'linear-gradient(145deg, #FFFFFF 0%, #F8FAFC 100%)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid rgba(244, 67, 54, 0.1)',
                '&:hover': {
                  boxShadow: '0 4px 16px rgba(244, 67, 54, 0.15)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box 
                  sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    backgroundColor: 'error.main',
                    color: 'white',
                  }}
                >
                  <TrendingDownIcon />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="error.main">
                    {formatCompactCurrency(shrinkageData.total_shrinkage_value || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    Total Shrinkage
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                p: 3, 
                borderRadius: 3,
                background: 'linear-gradient(145deg, #FFFFFF 0%, #F8FAFC 100%)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid rgba(255, 152, 0, 0.1)',
                '&:hover': {
                  boxShadow: '0 4px 16px rgba(255, 152, 0, 0.15)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box 
                  sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    backgroundColor: 'warning.main',
                    color: 'white',
                  }}
                >
                  <WarningIcon />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="warning.main">
                    {shrinkageData.shrinkage_percentage?.toFixed(1) || 0}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    Shrinkage Rate
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                p: 3, 
                borderRadius: 3,
                background: 'linear-gradient(145deg, #FFFFFF 0%, #F8FAFC 100%)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid rgba(33, 150, 243, 0.1)',
                '&:hover': {
                  boxShadow: '0 4px 16px rgba(33, 150, 243, 0.15)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box 
                  sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    backgroundColor: 'primary.main',
                    color: 'white',
                  }}
                >
                  <EditIcon />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="primary.main">
                    {totalRows}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    Total Adjustments
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                p: 3, 
                borderRadius: 3,
                background: 'linear-gradient(145deg, #FFFFFF 0%, #F8FAFC 100%)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid rgba(255, 152, 0, 0.1)',
                '&:hover': {
                  boxShadow: '0 4px 16px rgba(255, 152, 0, 0.15)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box 
                  sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    backgroundColor: 'warning.main',
                    color: 'white',
                  }}
                >
                  <WarningIcon />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="warning.main">
                    {adjustments.filter((adj: any) => adj.status === 'pending').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    Pending Approval
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Card 
        sx={{ 
          p: 3, 
          mb: 3,
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid rgba(0,0,0,0.08)',
        }}
      >
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2, color: 'primary.main' }}>
          Filters & Search
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Adjustment Type</InputLabel>
              <Select
                value={filters.adjustment_type}
                label="Adjustment Type"
                onChange={(e) => handleFilterChange('adjustment_type', e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="damage">Damage</MenuItem>
                <MenuItem value="loss">Loss</MenuItem>
                <MenuItem value="theft">Theft</MenuItem>
                <MenuItem value="found">Found</MenuItem>
                <MenuItem value="correction">Correction</MenuItem>
                <MenuItem value="manual">Manual</MenuItem>
                <MenuItem value="return">Return</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Start Date"
              type="date"
              value={filters.start_date}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="End Date"
              type="date"
              value={filters.end_date}
              onChange={(e) => handleFilterChange('end_date', e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Card>

      {/* Loading indicator */}
      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Data Table */}
      <Card 
        sx={{ 
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow 
                sx={{ 
                  backgroundColor: '#0d47a1',
                  '& .MuiTableCell-head': {
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    borderBottom: 'none',
                  },
                }}
              >
                <TableCell>Product Details</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Quantity</TableCell>
                <TableCell align="right">Cost Impact</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {adjustments.map((adjustment: any, index: number) => (
                <TableRow
                  key={adjustment.id}
                  hover
                  sx={{
                    '&:hover': {
                      backgroundColor: 'rgba(33, 150, 243, 0.04)',
                    },
                    borderBottom: index === adjustments.length - 1 ? 'none' : '1px solid rgba(0,0,0,0.06)',
                  }}
                >
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {adjustment.product_name || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {adjustment.sku || 'N/A'} - {adjustment.variant_name || 'N/A'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={adjustment.adjustment_type || 'unknown'}
                      size="small"
                      color={getAdjustmentTypeColor(adjustment.adjustment_type) as any}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      color={adjustment.quantity > 0 ? 'success.main' : 'error.main'}
                    >
                      {adjustment.quantity > 0 ? '+' : ''}{adjustment.quantity || 0}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      color={(adjustment.total_cost_impact || 0) > 0 ? 'success.main' : 'error.main'}
                    >
                      {formatCompactCurrency(adjustment.total_cost_impact || 0)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 200 }} noWrap>
                      {adjustment.reason || 'No reason provided'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={adjustment.status || 'unknown'}
                      size="small"
                      color={getStatusColor(adjustment.status) as any}
                      variant="filled"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {adjustment.created_by_admin_name || 'Unknown'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {adjustment.created_at ? new Date(adjustment.created_at).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedAdjustment(adjustment)
                            setIsViewOpen(true)
                          }}
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {adjustment.status === 'pending' && (
                        <Tooltip title="Approve">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleApprove(adjustment.id)}
                          >
                            <ApproveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          component="div"
          count={totalRows}
          rowsPerPage={pageSize}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setPageSize(parseInt(event.target.value, 10))
            setPage(0)
          }}
        />
      </Card>

      {/* Error handling */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Failed to load stock adjustments. Please try again.
        </Alert>
      )}
    </Box>
  )
}

export default StockAdjustmentsEnhanced